import { useEffect, useRef, useState } from "react";

import { anchorStyle } from "./anchor";
import { cn } from "../ui/cn";

const WIDTH = 264;
const HEIGHT = 150;

export interface LinkDraft {
  /** Viewport position of the button that opened it. */
  top: number;
  left: number;
  /** The selected text, used to prefill the label. */
  text: string;
}

/**
 * A small form for inserting a link, anchored under the toolbar button.
 *
 * This replaced `window.prompt`, which took the browser's modal styling, could
 * not offer the link text as a second field, and threw the agent out of the
 * page to answer it. Two fields next to the button they came from is both less
 * disruptive and more capable.
 *
 * The caller is responsible for holding the editor's selection Range across the
 * interaction -- focus moves to this input the moment it opens, and the
 * selection the link is meant to wrap would otherwise be gone by the time the
 * form is submitted.
 */
export function LinkPopover({
  draft,
  onSubmit,
  onCancel,
}: {
  draft: LinkDraft;
  onSubmit: (url: string, text: string) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState("");
  const [text, setText] = useState(draft.text);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCancel();
    };
    // Capture phase, so Escape closes this rather than the dialog behind it.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  // Clicking anywhere else dismisses, the way a popover is expected to.
  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      if (box.current?.contains(event.target as Node)) return;
      onCancel();
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [onCancel]);

  function submit(): void {
    const normalised = normaliseUrl(url);
    if (normalised === null) {
      setError("Use http://, https:// or mailto:");
      return;
    }
    onSubmit(normalised, text.trim());
  }

  return (
    <div
      ref={box}
      role="dialog"
      aria-label="Insert link"
      style={anchorStyle(draft.top, draft.left, WIDTH, HEIGHT)}
      className="fixed z-40 rounded-lg border border-line bg-surface p-2 shadow-lg"
    >
      <label className="mb-1 block text-[11px] text-muted" htmlFor="link-url">
        Link to
      </label>
      <input
        id="link-url"
        autoFocus
        value={url}
        onChange={(event) => {
          setUrl(event.target.value);
          setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          submit();
        }}
        placeholder="example.com"
        className={fieldClass}
      />

      <label className="mb-1 mt-2 block text-[11px] text-muted" htmlFor="link-text">
        Text <span className="text-faint">optional</span>
      </label>
      <input
        id="link-text"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          submit();
        }}
        placeholder="Shown instead of the address"
        className={fieldClass}
      />

      {error ? <p className="mt-1.5 text-[11px] text-red-600">{error}</p> : null}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={url.trim() === ""}
          className={cn(
            "rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white",
            "hover:bg-brand-700 disabled:opacity-40",
          )}
        >
          Insert
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const fieldClass = cn(
  "w-full rounded-md border border-line bg-surface px-2 py-1 text-xs text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

/**
 * Accept what someone would actually type, reject what cannot be linked.
 *
 * A bare domain gets https:// rather than an error -- typing "netxl.com" and
 * being told off is the commonest way this form could waste someone's time. A
 * `javascript:` URL returns null: the server strips it on the way out, but it
 * should never be offered in the first place.
 */
export function normaliseUrl(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return null;

  if (/^(https?:|mailto:)/i.test(value)) return value;

  // Anything with a scheme we do not allow is a refusal, not a candidate for
  // prefixing -- "javascript:alert(1)" must not become a https:// link.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;

  if (value.includes("@") && !value.includes("/")) return `mailto:${value}`;
  if (/^[^\s/]+\.[^\s/]{2,}/.test(value)) return `https://${value}`;

  return null;
}
